import { useCallback, useEffect, useRef, useState } from 'react'

import { onSearchCompanies, CompanySearchResult } from '../../lib/api'
import Spinner from '../utils/Spinner'

interface SearchBusinessesProps {
  searchTerm: string
  onSearchChange: (search: string) => void
  onSelectBusiness?: (business: CompanySearchResult) => void
}

export const SearchBusinesses = ({
  searchTerm,
  onSearchChange,
  onSelectBusiness,
}: SearchBusinessesProps) => {
  const [results, setResults] = useState<CompanySearchResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const searchTermRef = useRef(searchTerm)
  searchTermRef.current = searchTerm

  const handleSearch = useCallback(async ({ search }: { search: string }) => {
    if (!search.trim()) {
      setResults([])
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const data = await onSearchCompanies({ q: search })
      if (searchTermRef.current === search) {
        setResults(data.data.items)
      }
    } catch (err) {
      setError('Failed to search companies. Please try again.')
      console.error('Search error:', err)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    handleSearch({ search: searchTerm })
  }, [handleSearch, searchTerm])

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search for a business..."
          className="flex-1 p-2 border rounded"
        />
        <button
          onClick={() => handleSearch({ search: searchTerm })}
          disabled={isLoading}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300"
        >
          {isLoading ? 'Searching...' : 'Search'}
        </button>
      </div>

      {error && (
        <div className="text-red-500 p-2 bg-red-50 rounded">{error}</div>
      )}

      {results.length > 0 ? (
        <div className="space-y-2">
          {results.map((company) => (
            <div
              key={company.companyNumber}
              className="p-4 border rounded hover:bg-gray-50 cursor-pointer"
              onClick={() => onSelectBusiness?.(company)}
            >
              <h3 className="font-semibold">{company.title}</h3>
              <p className="text-sm text-gray-600">{company.address}</p>
              <div className="flex gap-4 mt-1 text-sm text-gray-500">
                <span>Company number: {company.companyNumber}</span>
                <span>
                  Created:{' '}
                  {new Date(company.dateOfCreation).toLocaleDateString()}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        isLoading && (
          <div className="text-gray-500 p-2 bg-gray-50 rounded flex justify-center items-center">
            <Spinner />
          </div>
        )
      )}
    </div>
  )
}
